import type { ScrollHintState, VisibleColumnRange } from "./kanban-scroll-types";

const EDGE_VISIBILITY_BUFFER_PX = 1;
const INITIAL_COLUMN_INDEX = 0;

export function getScrollHintState(
  scrollContainerNode: HTMLDivElement | null,
): ScrollHintState {
  if (!scrollContainerNode) {
    return {
      showLeft: false,
      showRight: false,
    };
  }

  const showLeft = scrollContainerNode.scrollLeft > EDGE_VISIBILITY_BUFFER_PX;
  const showRight =
    scrollContainerNode.scrollLeft + scrollContainerNode.clientWidth <
    scrollContainerNode.scrollWidth - EDGE_VISIBILITY_BUFFER_PX;

  return { showLeft, showRight };
}

export function getVisibleColumnRange(input: {
  columnNodes: HTMLDivElement[];
  scrollContainerNode: HTMLDivElement;
}): VisibleColumnRange | null {
  const viewportLeft = input.scrollContainerNode.scrollLeft;
  const viewportRight = viewportLeft + input.scrollContainerNode.clientWidth;
  const visibleIndices = input.columnNodes.reduce<number[]>(
    (indices, columnNode, columnIndex) => {
      const leftEdge = columnNode.offsetLeft;
      const rightEdge = leftEdge + columnNode.offsetWidth;
      const isVisible =
        rightEdge > viewportLeft + EDGE_VISIBILITY_BUFFER_PX &&
        leftEdge < viewportRight - EDGE_VISIBILITY_BUFFER_PX;

      if (!isVisible) {
        return indices;
      }

      return [...indices, columnIndex];
    },
    [],
  );

  if (visibleIndices.length === 0) {
    const closestIndex = getClosestColumnIndex({
      columnNodes: input.columnNodes,
      viewportLeft,
    });
    return {
      first: closestIndex,
      last: closestIndex,
    };
  }

  return {
    first: visibleIndices[0],
    last: visibleIndices[visibleIndices.length - 1],
  };
}

export function scrollToColumnIndex(input: {
  columnNode: HTMLDivElement | null | undefined;
  scrollContainerNode: HTMLDivElement | null;
}): void {
  if (!input.scrollContainerNode || !input.columnNode) {
    return;
  }

  input.scrollContainerNode.scrollTo({
    left: Math.max(0, input.columnNode.offsetLeft),
    behavior: "smooth",
  });
}

function getClosestColumnIndex(input: {
  columnNodes: HTMLDivElement[];
  viewportLeft: number;
}): number {
  return input.columnNodes.reduce<{
    index: number;
    distance: number;
  }>(
    (closest, columnNode, columnIndex) => {
      const distance = Math.abs(columnNode.offsetLeft - input.viewportLeft);
      if (distance >= closest.distance) {
        return closest;
      }

      return {
        index: columnIndex,
        distance,
      };
    },
    { index: INITIAL_COLUMN_INDEX, distance: Number.POSITIVE_INFINITY },
  ).index;
}
